import React from 'react';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import {
  PapiUser,
  PapiBadge,
  PapiEditorText,
  PapiDeviation,
} from '@wix/da-types-papi';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import DeviantIdViewer from '@wix/da-shared-react/pkg/DaEditorWrapper/Viewer/DeviantId';
import Badge from '@wix/da-shared-react/pkg/Badge';
import ElementQueries from '@wix/da-shared-react/pkg/ElementQueries';
import BirthdayCakeIcon from '@wix/da-shared-react/pkg/Icons/BirthdayCake';
import DeviantArtLogoIcon from '@wix/da-shared-react/pkg/Icons/DeviantArtLogo';
import LocationPinIcon from '@wix/da-shared-react/pkg/Icons/LocationPin';
import LinkIcon from '@wix/da-shared-react/pkg/Icons/Link';
import Interests from './Interests';
import Flex from '@wix/da-shared-react/pkg/Flex';
import OverflowedContent from '@wix/da-shared-react/pkg/OverflowedContent';
import UserLink from '@wix/da-shared-react/pkg/User/Link';
import { dateToDistanceTrans } from '@wix/da-shared-react/pkg/Timestamp/functions';
import SocialLink from './SocialLink';
import {
  Url,
  USER_PROFILE_SHOP_SUB_PATHS,
  USER_PROFILE_SECTION_PATHS,
} from '@wix/da-url';
import { getFeature } from '@wix/da-shared-react/pkg/DaEditorWrapper/Features';
import { isEditorTextEmpty } from '@wix/da-shared-react/pkg/DaEditorWrapper/utils';
import { FIRST_IMAGE_FEATURE } from '@wix/da-shared-react/pkg/DaEditorWrapper/Features/firstImage';
import DeviationDumbView from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import Button from '@wix/da-shared-react/pkg/Button';
import { withMobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import MediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import { BiEventType } from '@wix/da-shared-react/pkg/biLogger';

import { formatDateOfBirth, formatExternalUrl } from './utils';

import s from './AboutMe.scss';

export interface Props {
  moduleId: number;
  isWide?: boolean;
  isEmpty?: boolean;
  withBio?: boolean;
  user: PapiUser;
  isCurrentUserProfileOwner: boolean;
  isArtist?: boolean;
  isOpenForCommissions?: boolean;
  hasActiveCommissions?: boolean;
  level?: number;
  specialty?: number;
  website?: string;
  websiteLabel?: string;
  dobDay?: number;
  dobMonth?: number;
  dobYear?: number;
  countryId?: number;
  countrycode?: string;
  deviantFor: number; // duration in seconds
  devidDeviation?: PapiDeviation;
  bio?: PapiEditorText;
  badges?: PapiBadge[];
  showBadges: boolean;
  isMobile?: boolean;
  interests?: {
    id: number;
    value: string;
  }[];
  socialLinks?: {
    id: number;
    value?: string;
  }[];
  onStopEditing: () => void;
  hasJoinedWithEclipse?: boolean;
}

export interface State {
  visibleBadgesCount?: number;
  showAllBadges: boolean;
}

export class AboutMe extends React.Component<
  Props & WithTranslation & RouteComponentProps<any>,
  State
> {
  static defaultProps = {
    withBio: true,
  };
  state = {
    visibleBadgesCount: undefined,
    showAllBadges: false,
  };
  private badgeRefs = new Set<HTMLElement | null>();
  private moreBadgesIconRef: React.RefObject<
    HTMLButtonElement
  > = React.createRef();

  componentDidUpdate(nextProps, nextState) {
    this.updateVisibleBadgesCount();
  }

  render() {
    const {
      isWide,
      withBio,
      bio,
      user,
      countryId,
      dobDay,
      dobMonth,
      deviantFor,
      isOpenForCommissions,
      hasActiveCommissions,
      socialLinks,
      interests,
      showBadges: showBadgesSetting,
      isMobile,
    } = this.props;

    const hasSocialLinks = socialLinks && socialLinks.length > 0;
    const hasUserInfo = (dobDay && dobMonth) || !!countryId || deviantFor;
    const hasArtistInfo = this.props.isArtist;
    const hasInterests = interests && interests.length > 0;

    const showArtistInfoLine = hasArtistInfo && (!isMobile || isWide);
    const showUserInfoLine = hasUserInfo && (!isMobile || isWide);
    const showSocialLinks = hasSocialLinks && (!isMobile || isWide);
    const showBadges = showBadgesSetting && (!isMobile || isWide);
    const showInterests = hasInterests && isWide;
    const showBio = bio && withBio;
    const showCommissionsButton =
      !isMobile && !isWide && isOpenForCommissions && hasActiveCommissions;
    if (
      !showArtistInfoLine &&
      !showUserInfoLine &&
      !showSocialLinks &&
      !showBadges &&
      !showInterests &&
      !showBio &&
      !showCommissionsButton &&
      !isWide
    ) {
      return null;
    }
    const showBioAbove = !isMobile && !isWide;

    return (
      <ElementQueries
        className={classnames(
          s['root'],
          isWide && s['wide'],
          withBio && s['with-bio']
        )}
        widthBreakpoints={[999, 420]}
        onSettled={this.updateVisibleBadgesCount}
      >
        <div className={s['body']}>
          {isWide && showArtistInfoLine && (
            <div className={s['artist-info']}>
              {isMobile && (
                <UserLink
                  user={user}
                  className={s['user-link']}
                  symbolClassName={s['user-symbol']}
                  withTooltip={false}
                />
              )}
              {this.renderArtistInfoLine()}
            </div>
          )}
          {showBio && showBioAbove && this.renderBio()}
          {showUserInfoLine && this.renderUserInfoLine()}
          {showSocialLinks && this.renderSocialLinks()}
          {showBadges && this.renderBadges()}
          {showBio && !showBioAbove && this.renderBio()}
          {showInterests && this.renderInterests()}
          {showCommissionsButton && this.renderCommissionButton()}
        </div>
      </ElementQueries>
    );
  }

  renderLargeUsername() {
    return (
      <UserLink
        className={s['user-link-text']}
        symbolClassName={s['user-link-icon']}
        user={this.props.user}
        withTooltip={false}
      />
    );
  }

  renderBadges = () => {
    const { badges, user, t } = this.props;
    if (!badges) {
      return null;
    }
    const visibleBadgesCount = this.state.visibleBadgesCount || badges.length;
    const badgesNotShownCount = badges.length - visibleBadgesCount;

    return (
      <div
        className={classnames(
          s['line'],
          s['badges-block'],
          s['separated-block']
        )}
      >
        <div className={s['label']}>{t!('widgets.aboutMe.myBadges')}</div>
        <div
          className={classnames(
            s['badges-container'],
            this.state.showAllBadges && s['show-all']
          )}
        >
          {(() => {
            const elementList = [] as any;
            badges.forEach((badge, index) => {
              if (badgesNotShownCount > 0 && index === visibleBadgesCount - 1) {
                elementList.push(
                  <button
                    key="more-badges"
                    className={s['more-badges-icon']}
                    ref={this.moreBadgesIconRef}
                    onClick={this.showAllBadges}
                  >
                    +{badgesNotShownCount}
                  </button>
                );
              }
              elementList.push(
                <div
                  key={index}
                  className={s['badge-icon-wrapper']}
                  ref={this.storeBadgeRef}
                >
                  <a href={user && Url.badgesLink(user.username, badge.id)}>
                    <Badge {...badge} className={s['badge-icon']} />
                  </a>
                </div>
              );
            });
            return elementList;
          })()}
        </div>
      </div>
    );
  };

  storeBadgeRef = (node: HTMLElement | null) => {
    this.badgeRefs.add(node);
  };

  showAllBadges = () => {
    this.setState({
      showAllBadges: true,
      visibleBadgesCount: this.props.badges!.length,
    });
  };

  renderInterests = () => {
    if (!this.props.interests) {
      return null;
    }
    return (
      <Interests
        className={classnames(s['interests-block'], s['separated-block'])}
        interests={this.props.interests}
      />
    );
  };

  renderArtistInfoLine() {
    const { level, specialty } = this.props;
    const t = this.props.t!;
    return (
      <div className={classnames(s['line'], s['artist-info-block'])}>
        {t('widgets.aboutMe.artist')}
        {!!level && (
          <>
            <span className={s['artist-info-separator']}>{'//'}</span>

            {t(`widgets.aboutMe.levels.${level}`)}
          </>
        )}
        {!!specialty && (
          <>
            <span className={s['artist-info-separator']}>{'//'}</span>

            {t(`widgets.aboutMe.specialties.${specialty}`)}
          </>
        )}
      </div>
    );
  }

  renderUserInfoLine() {
    const {
      dobDay,
      dobMonth,
      dobYear,
      countryId,
      countrycode,
      website,
      websiteLabel,
      t,
      isMobile,
    } = this.props;
    // TODO make api just return the date...
    const joinDate = new Date();
    joinDate.setTime(joinDate.getTime() - this.props.deviantFor * 1000);
    const deviantFor = dateToDistanceTrans(joinDate.toISOString(), isMobile);

    return (
      <div className={classnames(s['user-info-block'], s['line'])}>
        {dobDay && (
          <div className={s['user-info-group']}>
            <BirthdayCakeIcon
              className={classnames(s['tiny-icon'], s['birthday-cake-icon'])}
            />
            <span className={s['user-info']}>
              {formatDateOfBirth(dobDay, dobMonth, dobYear)}
            </span>
          </div>
        )}
        {!!countryId && (
          <div className={s['user-info-group']}>
            <LocationPinIcon
              className={classnames(s['tiny-icon'], s['location-pin-icon'])}
            />
            <span className={s['user-info']}>
              {t!(`widgets.aboutMe.country.${countrycode || 'unknown'}`)}
            </span>
          </div>
        )}
        <div className={s['user-info-group']}>
          <DeviantArtLogoIcon
            className={classnames(s['tiny-icon'], s['da-logo-icon'])}
          />
          <span className={s['user-info']}>
            {t!('widgets.aboutMe.deviantFor', {
              deviantFor: t(deviantFor.key, {
                count: deviantFor.value,
              }),
            })}
          </span>
        </div>
        {website && (
          <div className={s['user-info-group']}>
            <LinkIcon
              className={classnames(s['tiny-icon'], s['website-icon'])}
            />
            <a
              href={formatExternalUrl(website)}
              className={s['user-info']}
              rel="noopener noreferrer nofollow"
              target="_blank"
            >
              {websiteLabel || website}
            </a>
          </div>
        )}
      </div>
    );
  }

  renderSocialLinks() {
    const {
      t,
      socialLinks,
      user: { username },
      website,
    } = this.props;
    if (!socialLinks || !socialLinks.length) {
      return null;
    }
    return (
      <div className={classnames(s['line'], s['social-links-block'])}>
        <div className={s['label']}>{t!('widgets.aboutMe.followMe')}</div>
        <div className={s['social-links']}>
          {socialLinks.map(
            (socialLink, index) =>
              socialLink.value && (
                <SocialLink
                  key={index}
                  username={username}
                  networkId={socialLink.id}
                  url={socialLink.value}
                  className={s['social-icon']}
                />
              )
          )}
          {website && (
            <SocialLink
              key={socialLinks.length}
              username={username}
              url={website}
              className={s['social-icon']}
            />
          )}
        </div>
      </div>
    );
  }

  renderBio() {
    const { isWide, bio, devidDeviation, user, isMobile } = this.props;
    const t = this.props.t!;

    if (!bio) {
      return null;
    }

    let firstImage;
    let firstImageDeviation;
    const isDraftJs = bio!.html.type === 'draft';
    if (isDraftJs) {
      firstImage = getFeature(FIRST_IMAGE_FEATURE, bio.html.features);
      firstImageDeviation = firstImage && firstImage.data.deviation;
    }

    // Allow devID for legacy bio
    if (!firstImageDeviation && devidDeviation && !isDraftJs) {
      firstImageDeviation = devidDeviation;
    }

    const isBioEmpty = isEditorTextEmpty(bio);

    const aboutUserLink = Url.userLink(
      user.username,
      `${USER_PROFILE_SECTION_PATHS.ABOUT}`
    );

    if (
      (isBioEmpty && isWide) ||
      (isBioEmpty && !isWide && !firstImageDeviation)
    ) {
      return null;
    }

    return (
      <div
        className={classnames(s['line'], s['bio-block'], s['separated-block'])}
      >
        <Flex direction="column" fullWidth>
          {isMobile && isWide && (
            <div className={s['bio-header']}>
              {t('widgets.aboutMe.aboutTheArtist')}
            </div>
          )}
          <Flex
            direction={isMobile || isWide ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems={'flex-start'}
          >
            {!isWide &&
              firstImageDeviation &&
              this.renderBioImage(firstImageDeviation)}
            {!isBioEmpty && !isWide && (
              <OverflowedContent
                className={s['bio-collapsed']}
                maxHeight={parseFloat(
                  s[`bio-max-height-collapsed${isMobile ? '-mobile' : ''}`]
                )}
              >
                {isOverflowed => (
                  <div>
                    <TextRenderer
                      className={s['bio-imageless']}
                      textContent={bio}
                      reactViewer={DeviantIdViewer}
                      htmlExcerptLegacy
                      legacyTheme={'default'}
                    />

                    {isOverflowed && (
                      <a
                        className={s['bio-clickable-overlay']}
                        href={aboutUserLink}
                      />
                    )}
                  </div>
                )}
              </OverflowedContent>
            )}
            {!isBioEmpty && isWide && (
              <div className={s['bio']}>
                {this.renderBioImage(!isDraftJs && devidDeviation)}

                <TextRenderer
                  textContent={bio}
                  reactViewer={DeviantIdViewer}
                  htmlExcerptLegacy
                  legacyTheme={'default'}
                />
              </div>
            )}
          </Flex>
        </Flex>
      </div>
    );
  }

  renderBioImage(deviation) {
    const { isWide } = this.props;

    if (!deviation) {
      return null;
    }

    let wideHeight;
    if (isWide) {
      const file = MediaService.getDeviationFile(deviation, 'preview');
      if (file) {
        wideHeight = file.height || 0;
      }
      if (!wideHeight) {
        return null;
      }
    }

    return (
      <DeviationDumbView
        height={isWide ? Math.min(250, wideHeight) : 92}
        deviation={deviation}
        className={classnames(
          s['profile-image'],
          isWide && s['profile-image-wide']
        )}
      />
    );
  }

  renderCommissionButton() {
    const { user } = this.props;
    return (
      <Button
        view="b18"
        onClick={this.openCommissions}
        className={s['commissions-button']}
        biData={{
          evid: BiEventType.OPEN_FOR_COMMISSION_CLICK,
          creatorid: user.useridUuid,
        }}
      >
        {this.props.t!('widgets.aboutMe.commissionsButton')}
      </Button>
    );
  }

  updateVisibleBadgesCount = () => {
    if (this.state.showAllBadges) {
      return;
    }
    // hide temporarily to capture how many badges fit into the row
    if (this.moreBadgesIconRef.current) {
      this.moreBadgesIconRef.current.style.display = 'none';
    }
    let visibleBadgesCount = 0;
    this.badgeRefs.forEach(badgeRef => {
      if (badgeRef && badgeRef.offsetTop === 0) {
        // if it's > 0, that means it jumped to next line which is hidden via CSS
        visibleBadgesCount++;
      }
    });
    if (this.moreBadgesIconRef.current) {
      this.moreBadgesIconRef.current.style.display = 'block';
    }
    if (visibleBadgesCount !== this.state.visibleBadgesCount) {
      this.setState({ visibleBadgesCount });
    }
  };
  openCommissions = () => {
    this.props.history.push(
      Url.userShopPath(
        this.props.user.username,
        USER_PROFILE_SHOP_SUB_PATHS.COMMISSIONS
      )
    );
  };
}

export default withRouter(
  withMobileContext(withTranslation()(AboutMe))
) as React.ComponentType<Props>;
